import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import styles from "./style.module.scss";
import {
  QuerySeasons,
  QueryEventsForSeason,
} from "../../../utils/graphql/hooks/eventManagementHooks";
import {
  QueryAvailablePerformanceMetricsSeasons,
  QueryAvailablePerformanceMetricsEvents,
  QueryPerformanceMetricsForEvent,
  MutateSavePerformanceMetricsData,
} from "../../../utils/graphql/hooks/performanceMetricsHooks";
import {
  setLoadedPerformanceMetricsData,
  viewLivePerformanceMetricsData,
  viewLoadedPerformanceMetricsData,
  setLoadingPage,
} from "../../../state/performanceMetricsReducer";
import { RootState } from "../../../state";
import { Season, Event } from "../../../types/eventManagement";

export default function MetricsSaveBar() {
  const dispatch = useDispatch();
  const { querySeasons, seasons } = QuerySeasons();
  const { queryEventsForSeason, events } = QueryEventsForSeason();

  const { querySeasons: queryLoadSeasons, seasons: loadSeasons } =
    QueryAvailablePerformanceMetricsSeasons();
  const { queryEvents: queryLoadEvents, events: loadEvents } =
    QueryAvailablePerformanceMetricsEvents();
  const { queryPerformanceMetricsForEvent } = QueryPerformanceMetricsForEvent();
  const savePerformanceMetricsData = MutateSavePerformanceMetricsData();

  const [selectedSaveSeason, setSelectedSaveSeason] = useState<number | null>(
    null,
  );
  const [selectedSaveEvent, setSelectedSaveEvent] = useState<string | null>(
    null,
  );
  const isViewingLoadedData = useSelector(
    (state: RootState) => state.performanceMetrics.isViewingLoadedData,
  );
  // Local state so the checkbox toggles immediately on click, ahead of the
  // deferred dispatch that actually performs the (slower) data-source switch.
  const [isLive, setIsLive] = useState(!isViewingLoadedData);
  const [showSaveControls, setShowSaveControls] = useState<boolean>(false);

  const [selectedLoadSeason, setSelectedLoadSeason] = useState<number | null>(
    null,
  );
  const [selectedLoadEvent, setSelectedLoadEvent] = useState<string | null>(
    null,
  );

  useEffect(() => {
    querySeasons();
  }, []);

  useEffect(() => {
    setIsLive(!isViewingLoadedData);
  }, [isViewingLoadedData]);

  const handleToggleLive = (nextIsLive: boolean) => {
    setIsLive(nextIsLive);
    dispatch(setLoadingPage(true));
    setTimeout(() => {
      if (!nextIsLive) {
        queryLoadSeasons();
        dispatch(viewLoadedPerformanceMetricsData());
      } else {
        dispatch(viewLivePerformanceMetricsData());
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dispatch(setLoadingPage(false));
        });
      });
    }, 0);
  };

  const handleSave = async () => {
    if (!selectedSaveSeason || !selectedSaveEvent) return;
    try {
      const result = await savePerformanceMetricsData({
        variables: {
          season: selectedSaveSeason,
          event: selectedSaveEvent,
        },
      });

      if (result.data?.savePerformanceMetricsData) {
        toast.success("Performance metrics saved successfully!");
        setShowSaveControls(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleLoad = async () => {
    if (!selectedLoadEvent) return;
    dispatch(setLoadingPage(true));
    const result = await queryPerformanceMetricsForEvent(selectedLoadEvent);
    if (result.data?.getPerformanceMetricsForEvent) {
      dispatch(
        setLoadedPerformanceMetricsData(
          result.data.getPerformanceMetricsForEvent,
        ),
      );
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        dispatch(setLoadingPage(false));
      });
    });
  };

  return (
    <div className={styles.container}>
      <label className={styles.checkbox} htmlFor="live">
        <input
          type="checkbox"
          id="live"
          checked={isLive}
          onChange={(e) => handleToggleLive(e.target.checked)}
        />
        Live
      </label>
      {isLive ? (
        showSaveControls ? (
          <div className={styles.controls}>
            <button
              className={styles.button}
              onClick={() => setShowSaveControls(false)}
            >
              Back
            </button>
            <select
              className={styles.selector}
              onChange={(e) => {
                const seasonNumber = Number(e.target.value);
                setSelectedSaveSeason(seasonNumber);
                setSelectedSaveEvent(null);
                queryEventsForSeason(seasonNumber);
              }}
              value={selectedSaveSeason ?? ""}
            >
              <option value="" disabled hidden>
                Season
              </option>
              {seasons?.map((season: Season) => (
                <option key={season.globalUniqueId} value={season.seasonNumber}>
                  {season.seasonNumber}
                </option>
              ))}
            </select>
            <select
              className={styles.selector}
              onChange={(e) => setSelectedSaveEvent(e.target.value)}
              value={selectedSaveEvent ?? ""}
              disabled={!selectedSaveSeason}
            >
              <option value="" disabled hidden>
                Event
              </option>
              {events?.map((event: Event) => (
                <option key={event.globalUniqueId} value={event.eventCode}>
                  {event.eventCode}
                </option>
              ))}
            </select>
            <button className={styles.button} onClick={handleSave}>
              Save
            </button>
          </div>
        ) : (
          <button
            className={styles.button}
            onClick={() => setShowSaveControls(true)}
          >
            Save Metrics
          </button>
        )
      ) : (
        <div className={styles.controls}>
          <select
            className={styles.selector}
            onChange={(e) => {
              const seasonNumber = Number(e.target.value);
              setSelectedLoadSeason(seasonNumber);
              setSelectedLoadEvent(null);
              queryLoadEvents(seasonNumber);
            }}
            value={selectedLoadSeason ?? ""}
          >
            <option value="" disabled hidden>
              Season
            </option>
            {loadSeasons?.map((season: number) => (
              <option key={season} value={season}>
                {season}
              </option>
            ))}
          </select>
          <select
            className={styles.selector}
            onChange={(e) => setSelectedLoadEvent(e.target.value)}
            value={selectedLoadEvent ?? ""}
            disabled={!selectedLoadSeason}
          >
            <option value="" disabled hidden>
              Event
            </option>
            {loadEvents?.map((event: string) => (
              <option key={event} value={event}>
                {event}
              </option>
            ))}
          </select>
          <button className={styles.button} onClick={handleLoad}>
            Load
          </button>
        </div>
      )}
    </div>
  );
}
